import getEnumValueByStringValue, { objectKeys } from '../../utils/object';
import { SectionBackgroundMode, SectionGutterMode } from '../atoms/Section/enums';
import { SectionWrapperProps } from '../atoms/Section/types';
import { REQUIRED_META_FIELDS } from './const';
import sectionsComponentsMap from './sectionsComponentsMap';
import { ComponentMeta, SectionMeta } from './types';

const getComponentNameFromPath = (path: string): string => {
  const [, sectionDirName] = path.split('/');

  return `${sectionDirName.charAt(0).toLowerCase()}${sectionDirName.slice(1)}`;
};

const metaContext = require.context('./templates', true, /meta.(ts|tsx)$/);

export const importSectionComponents = (): Record<string, ComponentMeta> => {
  const componentsMeta = metaContext.keys().reduce<Record<string, SectionMeta>>((result, path) => {
    const meta = metaContext(path);

    return {
      ...result,
      [getComponentNameFromPath(path)]: meta,
    };
  }, {});

  return objectKeys(sectionsComponentsMap).reduce<Record<string, ComponentMeta>>((result, key) => {
    const meta = componentsMeta[key];

    if (!meta) {
      throw new Error(`Meta is missing from component ${key}`);
    }

    const metaKeys = Object.keys(meta);
    const missingMetaField = REQUIRED_META_FIELDS.find(
      (metaField) => !metaKeys.includes(metaField)
    );

    if (missingMetaField) {
      throw new Error(`Component ${key} is missing ${missingMetaField} field from its meta.`);
    }

    return {
      ...result,
      [key]: {
        component: sectionsComponentsMap[key],
        meta: {
          ...meta,
        },
      },
    };
  }, {});
};

export const getSectionWrapperProps = <
  T extends {
    backgroundColor: string | undefined;
    gutterTop: string | undefined;
    gutterBottom: string | undefined;
    isFullWidth: boolean | undefined;
  }
>(
  section: T | undefined,
  defaultValues: SectionWrapperProps
): SectionWrapperProps => {
  const { backgroundColor, gutterTop, gutterBottom, isFullWidth } = section ?? {};

  return {
    backgroundMode:
      backgroundColor === 'default'
        ? defaultValues.backgroundMode
        : getEnumValueByStringValue(SectionBackgroundMode, backgroundColor) ??
          defaultValues.backgroundMode,
    gutterTop:
      gutterTop === 'default'
        ? defaultValues.gutterTop
        : getEnumValueByStringValue(SectionGutterMode, gutterTop) ?? defaultValues.gutterTop,
    gutterBottom:
      gutterBottom === 'default'
        ? defaultValues.gutterBottom
        : getEnumValueByStringValue(SectionGutterMode, gutterBottom) ?? defaultValues.gutterBottom,
    isFullWidth: !!(isFullWidth ?? defaultValues.isFullWidth),
  };
};
